import {
	Heading,
	Modal,
	ModalBody,
	ModalContent,
	ModalHeader,
	ModalOverlay,
} from '@chakra-ui/react';
import GenericForm, { IFormRows } from 'components/forms/GenericForm';
import { useCallback, useEffect, useState } from 'react';
import { renderFamily, renderSubFamily } from 'utils/forms/autocomplete';

import { makeGenericFormInputStructure } from '../../../../../utils/forms/defaultInputs';
import {
	defaultFormStyle,
	defaultInputStyle,
	formFlexProps,
	rowFlexProps,
} from '../../../../../utils/forms/defaultsProps';
import {
	validateMin,
	validateRequired,
} from '../../../../../utils/forms/validations';
import { useCreateFamily } from './hooks/useCreateFamily';
import { useCreateProductModel } from './hooks/useCreateProductModel';
import { useCreateSubFamily } from './hooks/useCreateSubFamily';
import { useUpdateFamily } from './hooks/useUpdateFamily';
import { useUpdateProductModel } from './hooks/useUpdateProductModel';
import { useUpdateSubFamily } from './hooks/useUpdateSubFamily';
import { familyMessages } from './variables/familyMessages';

interface ModalRegisterEditFamilyProps {
	type: 'family' | 'subFamily' | 'productModel';
	isOpen: boolean;
	onClose: () => void;
	autocompleteAdditionalData?: any;
}

const ModalRegisterEditFamily = (props: ModalRegisterEditFamilyProps) => {
	const { type, isOpen, onClose, autocompleteAdditionalData } = props;

	const [formInitialState, setFormInitialState] = useState({});
	const [payload, setPayload] = useState({});

	const [familyId, setFamilyId] = useState<number | null>(null);
	const [subFamilyId, setSubFamilyId] = useState<number | null>(null);
	const [productModelId, setProductModelId] = useState<number | null>(null);

	const { mutate: createFamily } = useCreateFamily();
	const { mutate: createSubFamily } = useCreateSubFamily();
	const { mutate: createProductModel } = useCreateProductModel();

	const { mutate: updateFamily } = useUpdateFamily();
	const { mutate: updateSubFamily } = useUpdateSubFamily(
		payload,
		subFamilyId
	);
	const { mutate: updateProductModel } = useUpdateProductModel(
		payload,
		productModelId
	);

	function getModalTitle() {
		if (type === 'family') {
			if (familyId) return familyMessages.SCREEN_TITLES.EDIT_FAMILY;
			return familyMessages.SCREEN_TITLES.CREATE_FAMILY;
		}
		if (type === 'subFamily') {
			if (subFamilyId) return familyMessages.SCREEN_TITLES.EDIT_SUBFAMILY;
			return familyMessages.SCREEN_TITLES.CREATE_SUBFAMILY;
		}
		if (type === 'productModel') {
			if (productModelId) return familyMessages.SCREEN_TITLES.EDIT_MODEL;
			return familyMessages.SCREEN_TITLES.CREATE_MODEL;
		}
	}

	const handleOnChangeFamily = useCallback((value: any) => {
		if (value) {
			setFamilyId(value?.id);
		} else {
			setFamilyId(null);
		}
	}, []);

	const handleOnChangeSubFamily = useCallback((value: any) => {
		if (value) {
			setSubFamilyId(value?.id);
		} else {
			setSubFamilyId(null);
		}
	}, []);

	const shouldClearAutocompleteSubFamily = useCallback(() => {
		if (!familyId) return false;

		return autocompleteAdditionalData?.subFamily?.familia_id !== familyId;
	}, [familyId, autocompleteAdditionalData?.subFamily?.familia_id]);

	const buildFamilyFormInput = useCallback((): IFormRows => {
		if (type !== 'family')
			return {
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'autocomplete',
							name: 'familia_id',
							label: 'Família',
							showRequiredOnLabel: false,
							disabled: () =>
								!!autocompleteAdditionalData?.family?.id,
							inputProps: {
								'data-test': `autocomplete-familia-${
									type === 'subFamily'
										? 'subfamilia'
										: 'modelo_de_produto'
								}-modal-familia`,
								...defaultInputStyle,
								searchType: 'familia',
								renderInputLabel: renderFamily,
								autocomplete: 'off',
								initialLabel: autocompleteAdditionalData?.family
									? renderFamily(
											autocompleteAdditionalData?.family
									  )
									: '',
								onChange: handleOnChangeFamily,
							},
						}),
					},
				],
			};

		return {
			type: 'common',
			rowFlexProps,
			columns: [
				{
					...makeGenericFormInputStructure({
						type: 'text',
						name: 'nome_familia',
						label: 'Nome da Família',
						showRequiredOnLabel: true,
						validate: {
							...validateRequired(),
							...validateMin(),
						},
						inputProps: {
							'data-test':
								'input-nome_da_familia-familia-modal-familia',
							...defaultInputStyle,
						},
					}),
				},
			],
		};
	}, [autocompleteAdditionalData?.family, handleOnChangeFamily, type]);

	const buildSubFamilyFormInput = useCallback((): IFormRows => {
		if (type !== 'subFamily')
			return {
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'autocomplete',
							name: 'sub_familia_id',
							label: 'Subfamília',
							showRequiredOnLabel: false,
							disabled: () =>
								!!autocompleteAdditionalData?.subFamily?.id,
							inputProps: {
								'data-test':
									'autocomplete-subfamilia-modelo_de_produto-modal-familia',
								...defaultInputStyle,
								searchType: 'sub_familia',
								renderInputLabel: renderSubFamily,
								autocomplete: 'off',
								initialLabel:
									autocompleteAdditionalData?.subFamily
										? renderSubFamily(
												autocompleteAdditionalData?.subFamily
										  )
										: '',
								onChange: handleOnChangeSubFamily,
								extraRequestData: {
									familia_id: familyId,
								},
								clearInput: shouldClearAutocompleteSubFamily,
							},
						}),
					},
				],
			};

		return {
			type: 'common',
			rowFlexProps,
			columns: [
				{
					...makeGenericFormInputStructure({
						type: 'text',
						name: 'nome_sub_familia',
						label: 'Nome da Subfamília',
						showRequiredOnLabel: true,
						validate: {
							...validateRequired(),
							...validateMin(),
						},
						inputProps: {
							'data-test':
								'input-nome_da_subfamilia-subfamilia-modal-familia',
							...defaultInputStyle,
						},
					}),
				},
			],
		};
	}, [
		autocompleteAdditionalData?.subFamily,
		handleOnChangeSubFamily,
		shouldClearAutocompleteSubFamily,
		familyId,
		type,
	]);

	const buildProductModelFormInput = useCallback((): IFormRows => {
		return {
			type: 'common',
			rowFlexProps,
			columns: [
				{
					...makeGenericFormInputStructure({
						type: 'text',
						name: 'nome_modelo',
						label: 'Nome do Modelo',
						showRequiredOnLabel: true,
						validate: {
							...validateRequired(),
							...validateMin(),
						},
						inputProps: {
							'data-test':
								'input-nome_do_modelo-modelo_de_produto-modal-familia',
							...defaultInputStyle,
							placeholder: 'Nome do Modelo',
						},
					}),
				},
			],
		};
	}, []);

	const renderFormRows = useCallback((): IFormRows[] => {
		switch (type) {
			case 'family':
				return [buildFamilyFormInput()];
			case 'subFamily':
				return [buildSubFamilyFormInput()];
			case 'productModel':
				return [buildProductModelFormInput()];
			default:
				return [];
		}
	}, [
		buildFamilyFormInput,
		buildProductModelFormInput,
		buildSubFamilyFormInput,
		type,
	]);

	const onSubmit = useCallback(
		values => {
			if (type === 'family') {
				if (familyId) {
					const payload = {
						nome_familia: values?.nome_familia,
					};

					updateFamily({ body: payload, id: familyId });
				} else {
					createFamily(values);
				}
			}

			if (type === 'subFamily') {
				if (subFamilyId) {
					setPayload({
						familia: values?.familia_id || null,
						nome: values?.nome_sub_familia,
					});
					updateSubFamily();
				} else {
					createSubFamily({
						...values,
						familia_id: values?.familia_id || null,
					});
				}
			}

			if (type === 'productModel') {
				if (productModelId) {
					setPayload({
						...values,
						familia_id: values?.familia_id || null,
						sub_familia_id: values?.sub_familia_id || null,
					});
					updateProductModel();
				} else {
					createProductModel({
						...values,
						familia_id: values?.familia_id || null,
						sub_familia_id: values?.sub_familia_id || null,
					});
				}
			}

			onClose();
		},
		[
			createFamily,
			createSubFamily,
			createProductModel,
			familyId,
			onClose,
			productModelId,
			subFamilyId,
			type,
			updateFamily,
			updateSubFamily,
			updateProductModel,
		]
	);

	useEffect(() => {
		setFamilyId(autocompleteAdditionalData?.id || null);
		setSubFamilyId(autocompleteAdditionalData?.id || null);
		setProductModelId(autocompleteAdditionalData?.id || null);

		switch (type) {
			case 'family':
				setFormInitialState({
					nome_familia:
						autocompleteAdditionalData?.nome_familia || '',
				});
				break;
			case 'subFamily':
				setFormInitialState({
					familia_id: autocompleteAdditionalData?.familia_id || null,
					nome_sub_familia:
						autocompleteAdditionalData?.nome_sub_familia || '',
				});
				break;
			case 'productModel':
				setFormInitialState({
					familia_id: autocompleteAdditionalData?.familia_id || null,
					sub_familia_id:
						autocompleteAdditionalData?.sub_familia_id || null,
					nome_modelo: autocompleteAdditionalData?.nome_modelo || '',
				});
				break;
		}
	}, [
		autocompleteAdditionalData?.id,
		autocompleteAdditionalData?.nome_familia,
		autocompleteAdditionalData?.nome_sub_familia,
		autocompleteAdditionalData?.nome_modelo,
		autocompleteAdditionalData?.familia_id,
		autocompleteAdditionalData?.sub_familia_id,
		type,
	]);

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			isCentered
		>
			<ModalOverlay />
			<ModalContent
				borderRadius={20}
				pt='20px'
			>
				<ModalHeader>
					<Heading
						color='gray.700'
						mb={2}
						size='md'
					>
						{getModalTitle()}
					</Heading>
				</ModalHeader>

				<ModalBody>
					<GenericForm
						dataTestProps={{
							cancel: 'button-cancelar-modal-familia',
							save: 'button-salvar-modal-familia',
						}}
						initialState={formInitialState}
						formFlexProps={formFlexProps}
						rows={renderFormRows()}
						style={defaultFormStyle}
						onSubmit={onSubmit}
						onCancel={onClose}
					/>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
};

export default ModalRegisterEditFamily;
